import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import { useAuthStore } from '../store/authStore'
import { Badge } from '../components/Badge'

interface MyProfile {
  user_id: string
  email: string | null
  full_name: string | null
  phone: string | null
  roles: string[]
  active: boolean
  is_placeholder: boolean
  activated_at: string | null
  google_account: string | null
  ios_account: string | null
}

const profileApi = {
  get: () => api.get<MyProfile>('/users/me'),
  update: (body: { full_name?: string; phone?: string; google_account?: string; ios_account?: string }) =>
    api.patch<MyProfile>('/users/me', body),
  changePassword: (body: { current_password: string; new_password: string }) =>
    api.post('/users/me/change-password', body),
}

const ROLE_LABELS: Record<string, string> = {
  admin:         'Super Admin',
  company_admin: 'Admin',
  executive:     'Direktor',
  analyst:       'Analitičar',
  dispatcher:    'Dispečer',
  meter_reader:  'Inkasant',
  scada_operator: 'SCADA operater',
  scada_repair:   'SCADA serviser',
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden flex-1 min-w-[280px]">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  )
}

function ProfileSection({ profile }: { profile: MyProfile }) {
  const qc = useQueryClient()
  const { user, setUser } = useAuthStore()

  const [fullName, setFullName]           = useState(profile.full_name ?? '')
  const [phone, setPhone]                 = useState(profile.phone ?? '')
  const [googleAccount, setGoogleAccount] = useState(profile.google_account ?? '')
  const [iosAccount, setIosAccount]       = useState(profile.ios_account ?? '')
  const [saved, setSaved]                 = useState(false)

  const mutation = useMutation({
    mutationFn: () => profileApi.update({
      full_name:      fullName.trim() || undefined,
      phone:          phone.trim() || undefined,
      google_account: googleAccount.trim() || undefined,
      ios_account:    iosAccount.trim() || undefined,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['me'] })
      if (user) {
        setUser({ ...user, fullName: res.data.full_name ?? user.fullName })
      }
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const dirty =
    fullName.trim()     !== (profile.full_name ?? '') ||
    phone.trim()        !== (profile.phone ?? '') ||
    googleAccount.trim() !== (profile.google_account ?? '') ||
    iosAccount.trim()    !== (profile.ios_account ?? '')

  return (
    <Section title="Profil">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-500 mb-1">E-mail</label>
          <input
            type="text"
            value={profile.email ?? ''}
            readOnly
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-default"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Ime i prezime</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Vaše ime"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Telefon</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+381 60 123 4567"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="sm:col-span-2">
          <p className="text-xs font-medium text-gray-500 mb-2">Potrebno za instalaciju mobilne aplikacije</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Google-konto (Android)</label>
          <input
            type="email"
            value={googleAccount}
            onChange={(e) => setGoogleAccount(e.target.value)}
            placeholder="korisnik@gmail.com"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Apple ID (iOS)</label>
          <input
            type="email"
            value={iosAccount}
            onChange={(e) => setIosAccount(e.target.value)}
            placeholder="korisnik@icloud.com"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Uloge</label>
          <div className="flex flex-wrap gap-1.5">
            {profile.roles.map((r) => (
              <Badge key={r} tone="info">{ROLE_LABELS[r] ?? r}</Badge>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={() => mutation.mutate()}
          disabled={!dirty || mutation.isPending}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-cyan text-brand-midnight hover:bg-brand-cyan-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {mutation.isPending ? 'Čuvanje…' : 'Sačuvaj izmene'}
        </button>
        {saved && <span className="text-sm text-emerald-600 font-medium">Sačuvano</span>}
        {mutation.isError && (
          <span className="text-sm text-red-600">
            {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška'}
          </span>
        )}
      </div>
    </Section>
  )
}

function PasswordSection({ isPlaceholder }: { isPlaceholder: boolean }) {
  const [current, setCurrent] = useState('')
  const [next, setNext]       = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone]       = useState(false)

  const mutation = useMutation({
    mutationFn: () =>
      profileApi.changePassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setCurrent(''); setNext(''); setConfirm('')
      setDone(true)
      setTimeout(() => setDone(false), 4000)
    },
  })

  const mismatch  = next.length > 0 && confirm.length > 0 && next !== confirm
  const canSubmit = !isPlaceholder && current && next.length >= 8 && next === confirm && !mutation.isPending

  return (
    <Section title="Promena lozinke">
      {isPlaceholder && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
          Nalog je privremeni. Aktivirajte nalog putem e-maila pre nego što promenite lozinku.
        </p>
      )}
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Trenutna lozinka</label>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} disabled={isPlaceholder}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Nova lozinka</label>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} disabled={isPlaceholder}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400" />
          {next.length > 0 && next.length < 8 && <p className="mt-1 text-xs text-red-500">Minimalno 8 karaktera</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Potvrdite lozinku</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={isPlaceholder}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400" />
          {mismatch && <p className="mt-1 text-xs text-red-500">Lozinke se ne poklapaju</p>}
        </div>
      </div>
      <div className="mt-5 flex items-center gap-3">
        <button onClick={() => mutation.mutate()} disabled={!canSubmit}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-cyan text-brand-midnight hover:bg-brand-cyan-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {mutation.isPending ? 'Menjanje…' : 'Promeni lozinku'}
        </button>
        {done && <span className="text-sm text-emerald-600 font-medium">Lozinka promenjena</span>}
        {mutation.isError && (
          <span className="text-sm text-red-600">
            {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška'}
          </span>
        )}
      </div>
    </Section>
  )
}

export default function AccountPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: () => profileApi.get().then((r) => r.data),
  })

  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-gray-400 text-sm">Učitavanje…</div>
  }

  if (isError || !data) {
    return <div className="flex items-center justify-center h-64 text-red-500 text-sm">Greška pri učitavanju profila.</div>
  }

  return (
    <div className="p-6 flex flex-wrap gap-6 items-start">
      <ProfileSection profile={data} />
      <PasswordSection isPlaceholder={data.is_placeholder} />
    </div>
  )
}
